// src/pages/Admin/PermissionsTab.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table, Tag, Space, Typography, message,
  Input, Button, Switch, Modal, Tooltip,
  Popconfirm, Segmented, Row, Col, Divider, Select,
} from 'antd';
import {
  SearchOutlined, SettingOutlined, ReloadOutlined,
  CrownOutlined, SafetyOutlined, GoogleOutlined, PlusOutlined,
} from '@ant-design/icons';
import client from '../../api/client';
import { employeesAPI } from '../../api/client';

const { Text } = Typography;

const MODULE_LABELS = {
  equipment:      'Оборудование',
  employees:      'Сотрудники',
  services:       'Сервисы',
  consumables:    'Расходники',
  acts:           'Акты выдачи',
  return_acts:    'Акты возврата',
  it_analytics:   'IT Аналитика',
  admin:          'Администрирование',
};

const MODULES = Object.keys(MODULE_LABELS);

function getCurrentUser() {
  return {
    is_super_admin: localStorage.getItem('is_super_admin') === 'true',
    is_admin:       localStorage.getItem('is_admin')       === 'true',
  };
}

// ─── Модалка ВЫДАЧИ доступа новому сотруднику ──────────────────────────────
function GrantAccessModal({ onClose }) {
  const queryClient = useQueryClient();
  const currentUser = getCurrentUser();

  const [employeeId, setEmployeeId] = useState(null);
  const [search, setSearch]         = useState('');
  const [roles, setRoles] = useState({
    can_login:      true,   // по умолчанию только вход
    is_admin:       false,
    is_super_admin: false,
    is_security:    false,
  });
  const [perms, setPerms] = useState(
    MODULES.reduce((acc, m) => ({ ...acc, [m]: 'none' }), {})
  );

  // Все сотрудники → фильтруем тех, у кого доступа ещё нет
  const { data: empData, isLoading: empLoading } = useQuery({
    queryKey: ['employees-for-grant', search],
    queryFn:  () => employeesAPI.getAll({
      search: search || undefined,
      is_active: true,
      limit: 500,
    }).then(r => r.data),
  });

  const candidates = ((empData?.items) || [])
    .filter(e => !e.can_login && e.email); // без доступа и с почтой

  const grantMutation = useMutation({
    mutationFn: async () => {
      // 1) вход + роли
      await client.patch(`/permissions/roles/${employeeId}`, roles);
      // 2) права по модулям (только если хоть что-то выставлено выше none)
      const hasPerms = MODULES.some(m => perms[m] && perms[m] !== 'none');
      if (hasPerms) {
        await client.put(`/permissions/${employeeId}`, { permissions: perms });
      }
    },
    onSuccess: () => {
      message.success('Доступ выдан');
      queryClient.invalidateQueries(['permissions-all']);
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка выдачи доступа'),
  });

  const setAllPerms = (level) => {
    const next = {};
    MODULES.forEach(m => {
      if (m === 'admin' && !currentUser.is_super_admin) {
        next[m] = perms[m] || 'none';
      } else {
        next[m] = level;
      }
    });
    setPerms(next);
  };

  const handleOk = () => {
    if (!employeeId) {
      message.warning('Выберите сотрудника');
      return;
    }
    grantMutation.mutate();
  };

  return (
    <Modal
      open
      title="Выдать доступ сотруднику"
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={grantMutation.isPending}
      okText="Выдать доступ"
      cancelText="Отмена"
      width={620}
    >
      {/* Выбор сотрудника */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>Сотрудник</Text>
        <Select
          showSearch
          value={employeeId}
          placeholder="Начните вводить имя или email"
          style={{ width: '100%' }}
          loading={empLoading}
          filterOption={false}
          onSearch={setSearch}
          onChange={setEmployeeId}
          notFoundContent={empLoading ? 'Загрузка…' : 'Нет сотрудников без доступа'}
          options={candidates.map(e => ({
            label: `${e.full_name} (${e.email})`,
            value: e.id,
          }))}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Показаны только сотрудники без доступа. Если человека нет — заведите его в разделе «Сотрудники».
        </Text>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Роли */}
      <Text strong style={{ display: 'block', marginBottom: 10 }}>Роли</Text>
      <Space direction="vertical" size={10} style={{ width: '100%', marginBottom: 16 }}>
        <Row align="middle">
          <Col span={18}>
            <Text>Вход в систему (через Google)</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Без этого доступа не будет</Text></div>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Switch
              checked={roles.can_login}
              onChange={(v) => setRoles(prev => ({
                ...prev,
                can_login: v,
                ...(v ? {} : { is_admin: false, is_super_admin: false, is_security: false }),
              }))}
            />
          </Col>
        </Row>
        <Row align="middle">
          <Col span={18}>
            <Text>Администратор</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Управление правами и справочниками</Text></div>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Switch
              checked={roles.is_admin}
              disabled={!roles.can_login || !currentUser.is_super_admin}
              onChange={(v) => setRoles(prev => ({ ...prev, is_admin: v }))}
            />
          </Col>
        </Row>
        <Row align="middle">
          <Col span={18}>
            <Text>Супер-администратор</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Полный доступ, назначение админов</Text></div>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Switch
              checked={roles.is_super_admin}
              disabled={!roles.can_login || !currentUser.is_super_admin}
              onChange={(v) => setRoles(prev => ({ ...prev, is_super_admin: v }))}
            />
          </Col>
        </Row>
        <Row align="middle">
          <Col span={18}>
            <Text>Служба безопасности</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Системная роль службы безопасности</Text></div>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Switch
              checked={roles.is_security}
              disabled={!roles.can_login}
              onChange={(v) => setRoles(prev => ({ ...prev, is_security: v }))}
            />
          </Col>
        </Row>
      </Space>

      <Divider style={{ margin: '12px 0' }} />

      {/* Права по модулям */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Text strong style={{ flex: 1 }}>Права по модулям</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>Выдать всем:</Text>
        <Button size="small" onClick={() => setAllPerms('edit')}>✏️ Полный</Button>
        <Button size="small" onClick={() => setAllPerms('view')}>👁 Просмотр</Button>
        <Button size="small" danger onClick={() => setAllPerms('none')}>✕ Закрыть</Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {MODULES.map(module => {
          const isAdminModule = module === 'admin';
          const disabled = isAdminModule && !currentUser.is_super_admin;
          return (
            <Row key={module} align="middle" gutter={16}>
              <Col span={10}>
                <Text style={{ fontSize: 13 }}>{MODULE_LABELS[module]}</Text>
                {isAdminModule && (
                  <div style={{ fontSize: 11, color: '#888' }}>только супер-админ</div>
                )}
              </Col>
              <Col span={14}>
                <Segmented
                  size="small"
                  disabled={disabled}
                  value={perms[module] || 'none'}
                  onChange={(val) => setPerms(prev => ({ ...prev, [module]: val }))}
                  options={[
                    { value: 'none', label: 'Нет' },
                    { value: 'view', label: '👁 Просмотр' },
                    { value: 'edit', label: '✏️ Полный' },
                  ]}
                />
              </Col>
            </Row>
          );
        })}
      </div>
    </Modal>
  );
}

// ─── Модалка редактирования прав уже существующего сотрудника ──────────────
function PermissionsModal({ employee, onClose }) {
  const queryClient = useQueryClient();
  const currentUser = getCurrentUser();
  const [perms, setPerms] = useState({ ...(employee.permissions || {}) });

  const saveMutation = useMutation({
    mutationFn: () => client.put(`/permissions/${employee.employee_id}`, { permissions: perms }),
    onSuccess: () => {
      message.success('Права сохранены');
      queryClient.invalidateQueries(['permissions-all']);
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка сохранения'),
  });

  const setAll = (level) => {
    const next = {};
    MODULES.forEach(m => {
      if (m === 'admin' && !currentUser.is_super_admin) {
        next[m] = perms[m] || 'none';
      } else {
        next[m] = level;
      }
    });
    setPerms(next);
  };

  return (
    <Modal
      open
      title={
        <div>
          <div style={{ fontWeight: 600 }}>{employee.full_name}</div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>{employee.email}</div>
        </div>
      }
      onCancel={onClose}
      onOk={() => saveMutation.mutate()}
      confirmLoading={saveMutation.isPending}
      okText="Сохранить"
      cancelText="Отмена"
      width={560}
    >
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Выдать всем:</Text>
        <Button size="small" onClick={() => setAll('edit')}>✏️ Полный доступ</Button>
        <Button size="small" onClick={() => setAll('view')}>👁 Только просмотр</Button>
        <Button size="small" danger onClick={() => setAll('none')}>✕ Закрыть всё</Button>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {MODULES.map(module => {
          const isAdminModule = module === 'admin';
          const disabled = isAdminModule && !currentUser.is_super_admin;
          const current = perms[module] || 'none';
          return (
            <Row key={module} align="middle" gutter={16}>
              <Col span={10}>
                <Text style={{ fontSize: 13 }}>{MODULE_LABELS[module]}</Text>
                {isAdminModule && (
                  <div style={{ fontSize: 11, color: '#888' }}>только супер-админ</div>
                )}
              </Col>
              <Col span={14}>
                <Segmented
                  size="small"
                  disabled={disabled}
                  value={current}
                  onChange={(val) => setPerms(prev => ({ ...prev, [module]: val }))}
                  options={[
                    { value: 'none', label: 'Нет' },
                    { value: 'view', label: '👁 Просмотр' },
                    { value: 'edit', label: '✏️ Полный' },
                  ]}
                />
              </Col>
            </Row>
          );
        })}
      </div>
    </Modal>
  );
}

// ─── Строка-переключатель роли ─────────────────────────────────────────────
function RoleSwitch({ label, tip, checked, disabled, loading, onChange }) {
  return (
    <Tooltip title={tip || ''}>
      <Space size={4}>
        <Text style={{ fontSize: 11, color: '#888' }}>{label}</Text>
        <Switch
          size="small"
          checked={!!checked}
          disabled={disabled || loading}
          onChange={onChange}
        />
      </Space>
    </Tooltip>
  );
}

export default function PermissionsTab() {
  const [search, setSearch]     = useState('');
  const [editing, setEditing]   = useState(null);
  const [granting, setGranting] = useState(false);
  const queryClient             = useQueryClient();
  const currentUser             = getCurrentUser();

  const { data, isLoading } = useQuery({
    queryKey: ['permissions-all'],
    queryFn:  () => client.get('/permissions/all').then(r => r.data),
  });

  const roleMutation = useMutation({
    mutationFn: ({ employee_id, ...fields }) =>
      client.patch(`/permissions/roles/${employee_id}`, fields),
    onSuccess: () => {
      message.success('Обновлено');
      queryClient.invalidateQueries(['permissions-all']);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const resetMutation = useMutation({
    mutationFn: (employee_id) => client.delete(`/permissions/${employee_id}`),
    onSuccess: () => {
      message.success('Права сброшены');
      queryClient.invalidateQueries(['permissions-all']);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const filtered = (data || []).filter(e =>
    !search ||
    e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase())
  );

  function permSummary(record) {
    if (record.is_super_admin || record.is_admin) {
      return <Tag color="gold">Полный доступ</Tag>;
    }
    const perms = record.permissions || {};
    const editCount = MODULES.filter(m => perms[m] === 'edit').length;
    const viewCount = MODULES.filter(m => perms[m] === 'view').length;
    const noneCount = MODULES.filter(m => !perms[m] || perms[m] === 'none').length;

    if (noneCount === MODULES.length) {
      return <Tag color="red">Нет доступа</Tag>;
    }
    return (
      <Space size={4}>
        {editCount > 0 && <Tag color="green">Полный: {editCount}</Tag>}
        {viewCount > 0 && <Tag color="blue">Просмотр: {viewCount}</Tag>}
        {noneCount > 0 && <Tag color="default">Закрыто: {noneCount}</Tag>}
      </Space>
    );
  }

  const columns = [
    {
      title: 'Сотрудник', key: 'name', width: 240,
      render: (_, r) => (
        <div>
          <Text strong>{r.full_name}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Роли и доступ', key: 'roles', width: 360,
      render: (_, r) => (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Space size={4} wrap>
            {r.is_super_admin && <Tag icon={<CrownOutlined />} color="gold">Супер-админ</Tag>}
            {r.is_admin       && <Tag icon={<SettingOutlined />} color="purple">Админ</Tag>}
            {r.is_security    && <Tag icon={<SafetyOutlined />} color="red">СБ</Tag>}
            {r.can_login && !r.is_super_admin && !r.is_admin && !r.is_security && (
              <Tag icon={<GoogleOutlined />} color="blue">Пользователь</Tag>
            )}
            {!r.can_login && <Tag color="default">Нет входа</Tag>}
          </Space>
          <Space size={12} wrap>
            <RoleSwitch
              label="Вход" tip="Доступ в систему через Google"
              checked={r.can_login}
              disabled={r.is_super_admin}
              loading={roleMutation.isPending}
              onChange={(v) => roleMutation.mutate({
                employee_id: r.employee_id,
                can_login: v,
                ...(v ? {} : { is_admin: false, is_super_admin: false, is_security: false }),
              })}
            />
            <RoleSwitch
              label="Админ" tip="Управление правами и справочниками"
              checked={r.is_admin}
              disabled={r.is_super_admin || !r.can_login || !currentUser.is_super_admin}
              loading={roleMutation.isPending}
              onChange={(v) => roleMutation.mutate({ employee_id: r.employee_id, is_admin: v })}
            />
            <RoleSwitch
              label="Супер" tip="Полный доступ, назначение админов"
              checked={r.is_super_admin}
              disabled={!r.can_login || !currentUser.is_super_admin}
              loading={roleMutation.isPending}
              onChange={(v) => roleMutation.mutate({ employee_id: r.employee_id, is_super_admin: v })}
            />
            <RoleSwitch
              label="СБ" tip="Системная роль службы безопасности"
              checked={r.is_security}
              disabled={r.is_super_admin || !r.can_login}
              loading={roleMutation.isPending}
              onChange={(v) => roleMutation.mutate({ employee_id: r.employee_id, is_security: v })}
            />
          </Space>
        </Space>
      ),
    },
    {
      title: 'Права по модулям', key: 'perms',
      render: (_, r) => permSummary(r),
    },
    {
      title: '', key: 'actions', width: 160,
      render: (_, r) => {
        if (r.is_super_admin) {
          return <Text type="secondary" style={{ fontSize: 12 }}>Полный доступ</Text>;
        }
        return (
          <Space>
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => setEditing(r)}
              disabled={!r.can_login}
            >
              Права
            </Button>
            <Tooltip title="Сбросить все права">
              <Popconfirm
                title="Сбросить все права на «Нет доступа»?"
                onConfirm={() => resetMutation.mutate(r.employee_id)}
                okText="Да" cancelText="Нет"
              >
                <Button size="small" icon={<ReloadOutlined />} danger />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">
          Управление доступом — <Text strong>{filtered.length}</Text> сотрудников
        </Text>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setGranting(true)}>
            Выдать доступ
          </Button>
          <Input
            placeholder="Поиск по имени или email"
            prefix={<SearchOutlined />}
            style={{ width: 280 }}
            value={search}
            allowClear
            onChange={e => setSearch(e.target.value)}
          />
        </Space>
      </div>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="employee_id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 20, showTotal: t => `${t} сотрудников` }}
        rowClassName={r => r.is_super_admin ? 'row-super-admin' : ''}
      />

      {granting && (
        <GrantAccessModal onClose={() => setGranting(false)} />
      )}

      {editing && (
        <PermissionsModal
          employee={editing}
          onClose={() => setEditing(null)}
        />
      )}

      <style>{`.row-super-admin td { background: #fffbe6 !important; }`}</style>
    </div>
  );
}